/**
 * ParticleBurst
 * Reusable one-shot particle burst. Fires when `trigger` flips true.
 *
 * Props:
 *   originRef     — ref to any Three.js Object3D; world position of spawn
 *   orientRef     — ref whose world quaternion determines emit direction (defaults to originRef)
 *   trigger       — boolean; rising edge fires the burst
 *   direction     — [x,y,z] local-space emit direction (default [0,1,0] = local +Y)
 *   spread        — cone half-angle in radians (default Math.PI = full sphere)
 *   count         — number of particles (default 40)
 *   speed         — velocity magnitude (default 0.06)
 *   lifetime      — seconds until fully faded (default 1.2)
 *   size          — point size in pixels (default 4)
 *   particleColor — css/hex color string (default '#aaddff')
 *   originOffset  — [x,y,z] offset applied to spawn position in world space (default [0,0,0])
 *   delay         — seconds to wait after trigger before firing (default 0)
 *   debug         — show arrow at spawn point indicating emit direction (default false)
 */

import { useRef, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { PointsNodeMaterial } from 'three/webgpu'
import { uniform, color, attribute, vec3 } from 'three/tsl'

const _wp  = new THREE.Vector3()
const _wq  = new THREE.Quaternion()
const _dir = new THREE.Vector3()
const _v   = new THREE.Vector3()
const _perp = new THREE.Vector3()

function getSpawnPoint(originRef, orientRef, originOffset, outPos, outQuat) {
  outPos.set(0, 0, 0)
  outQuat.identity()
  const ref = orientRef?.current ?? originRef?.current
  if (ref) ref.getWorldQuaternion(outQuat)
  if (originRef?.current) {
    outPos.set(...originOffset)
    outPos.applyMatrix4(originRef.current.matrixWorld)
  }
}

function fireBurst(s, originRef, orientRef, originOffset, direction, spread, minSpread, count, positions, worldVelocities, speed, geometry, opacityUniform) {
  if (originRef?.current) originRef.current.updateWorldMatrix(true, false)
  if (orientRef?.current) orientRef.current.updateWorldMatrix(true, false)
  getSpawnPoint(originRef, orientRef, originOffset, _wp, _wq)

  // Local emit direction rotated to world space
  _dir.set(...direction).normalize().applyQuaternion(_wq)

  const spreadRad = 0.003
  for (let i = 0; i < count; i++) {
    positions[i * 3]     = _wp.x + (Math.random() - 0.5) * spreadRad
    positions[i * 3 + 1] = _wp.y + (Math.random() - 0.5) * spreadRad
    positions[i * 3 + 2] = _wp.z + (Math.random() - 0.5) * spreadRad

    // Build velocity in a band around _dir — minSpread to spread creates a donut shape
    const theta = Math.random() * Math.PI * 2
    const phi   = minSpread + Math.random() * (spread - minSpread)
    // Perpendicular basis
    _perp.set(1, 0, 0)
    if (Math.abs(_dir.dot(_perp)) > 0.9) _perp.set(0, 1, 0)
    const right = new THREE.Vector3().crossVectors(_dir, _perp).normalize()
    const up    = new THREE.Vector3().crossVectors(right, _dir).normalize()
    const s2    = Math.sin(phi)
    _v.copy(_dir).multiplyScalar(Math.cos(phi))
      .addScaledVector(right, s2 * Math.cos(theta))
      .addScaledVector(up,    s2 * Math.sin(theta))
      .normalize()
      .multiplyScalar(speed * (0.5 + Math.random() * 0.5))

    worldVelocities[i * 3]     = _v.x
    worldVelocities[i * 3 + 1] = _v.y
    worldVelocities[i * 3 + 2] = _v.z
  }
  geometry.attributes.position.needsUpdate = true
  opacityUniform.value = 1
  s.active = true
  s.t      = 0
}

// Debug arrow — updates every frame showing spawn point and emit direction
function DebugArrow({ originRef, orientRef, originOffset, direction }) {
  const arrowRef = useRef(null)

  useEffect(() => {
    const arrow = new THREE.ArrowHelper(new THREE.Vector3(0, 1, 0), new THREE.Vector3(), 0.05, 0xff0000)
    arrowRef.current = arrow
    return () => {
      if (arrow.parent) arrow.parent.remove(arrow)
      arrow.dispose()
    }
  }, [])

  useFrame(({ scene }) => {
    if (!arrowRef.current) return
    getSpawnPoint(originRef, orientRef, originOffset, _wp, _wq)
    _dir.set(...direction).normalize().applyQuaternion(_wq)
    arrowRef.current.position.copy(_wp)
    arrowRef.current.setDirection(_dir)
    if (!arrowRef.current.parent) scene.add(arrowRef.current)
  })

  return null
}

export function ParticleBurst({
  originRef,
  orientRef     = null,
  trigger,
  direction     = [0, 1, 0],
  spread        = Math.PI,
  minSpread     = 0,
  count         = 40,
  speed         = 0.06,
  lifetime      = 1.2,
  size          = 4,
  particleColor = '#aaddff',
  originOffset  = [0, 0, 0],
  delay         = 0,
  debug         = false,
}) {
  const meshRef        = useRef()
  const stateRef       = useRef({ active: false, t: 0, pending: false, delayT: 0 })
  const prevTrigger    = useRef(false)

  const positions       = useMemo(() => new Float32Array(count * 3), [count])
  const worldVelocities = useMemo(() => new Float32Array(count * 3), [count])
  const sizes           = useMemo(() => Float32Array.from({ length: count }, () =>
    size * (0.67 + Math.random() * 0.66)
  ), [count, size])
  const brightnesses    = useMemo(() => Float32Array.from({ length: count }, () =>
    Math.random() * Math.random()  // squared random skews toward dim with occasional bright
  ), [count])

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position',    new THREE.BufferAttribute(positions, 3))
    g.setAttribute('aSize',       new THREE.BufferAttribute(sizes, 1))
    g.setAttribute('aBrightness', new THREE.BufferAttribute(brightnesses, 1))
    return g
  }, [positions, sizes, brightnesses])

  const opacityUniform = useMemo(() => uniform(0), [])

  const material = useMemo(() => {
    const mat = new PointsNodeMaterial()
    // Per-particle brightness: squared random skews toward dim with occasional blown-out flecks
    const c = new THREE.Color(particleColor)
    const base = vec3(c.r * 6, c.g * 6, c.b * 6)
    mat.colorNode = base.mul(attribute('aBrightness'))
    mat.opacityNode     = opacityUniform
    mat.pointSizeNode   = attribute('aSize')
    mat.transparent     = true
    mat.depthWrite      = false
    mat.sizeAttenuation = true
    return mat
  }, [particleColor, opacityUniform])

  useEffect(() => () => {
    geometry.dispose()
    material.dispose()
  }, [geometry, material])

  useFrame((_, delta) => {
    const s = stateRef.current

    if (trigger && !prevTrigger.current) {
      if (delay > 0) {
        s.pending = true
        s.delayT  = 0
      } else {
        fireBurst(s, originRef, orientRef, originOffset, direction, spread, minSpread, count, positions, worldVelocities, speed, geometry, opacityUniform)
      }
    }
    prevTrigger.current = trigger

    if (s.pending) {
      s.delayT += delta
      if (s.delayT >= delay) {
        s.pending = false
        fireBurst(s, originRef, orientRef, originOffset, direction, spread, minSpread, count, positions, worldVelocities, speed, geometry, opacityUniform)
      }
    }

    if (!s.active || !meshRef.current) return

    s.t += delta
    const progress = s.t / lifetime

    if (progress >= 1) {
      s.active             = false
      opacityUniform.value = 0
      return
    }

    opacityUniform.value = 1 - progress
    const ease = 1 - progress

    for (let i = 0; i < count; i++) {
      positions[i * 3]     += worldVelocities[i * 3]     * delta * ease
      positions[i * 3 + 1] += worldVelocities[i * 3 + 1] * delta * ease
      positions[i * 3 + 2] += worldVelocities[i * 3 + 2] * delta * ease
    }
    geometry.attributes.position.needsUpdate = true
  })

  return (
    <>
      <points ref={meshRef} geometry={geometry} material={material} frustumCulled={false} />
      {debug && <DebugArrow originRef={originRef} orientRef={orientRef} originOffset={originOffset} direction={direction} />}
    </>
  )
}
